import { HttpClient, provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { createErrorHandler } from '@sentry/angular';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getApp, initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';

import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  AuthService,
  FIREBASE_AUTH,
  FIREBASE_FIRESTORE,
  FIREBASE_FUNCTIONS,
  FIREBASE_STORAGE,
  initializeAuth,
} from '@verify/shared-components/services';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

registerLocaleData(localeNL, 'nl');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const app = initializeApp(environment.firebaseConfig);
if (environment.reCaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(environment.reCaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useValue: createErrorHandler() },
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    { provide: FIREBASE_AUTH, useFactory: () => getAuth() },
    {
      provide: FIREBASE_FUNCTIONS,
      useFactory: () => getFunctions(getApp(), environment.functionRegion),
    },
    {
      provide: FIREBASE_FIRESTORE,
      useFactory: () => getFirestore(getApp(), environment.database),
    },
    { provide: FIREBASE_STORAGE, useFactory: () => getStorage() },
    provideAnimationsAsync(),
    provideAppInitializer(() => initializeAuth(inject(AuthService))),
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
  ],
};
